import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';
import { sortByNewest } from '../utils/sortByNewest';

export const fetchMovies = createAsyncThunk('movies/fetch', async (params) => {
    const response = await api.get('/movies', { params });
    return response.data;
});

export const fetchMovie = createAsyncThunk('movies/fetchMovie', async ({ movieId }) => {
    const response = await api.get(`/movies/${movieId}`);
    return response.data;
});

export const updateMovie = createAsyncThunk('movies/update', async ({ token, movieId, body }) => {
    const response = await api.put(`/movies/${movieId}`, body);
    return response.data;
});

export const deleteMovie = createAsyncThunk('movies/delete', async ({ movieId }) => {
    const response = await api.delete(`/movies/${movieId}`);
    return response.data;
});

export const postNewVersion = createAsyncThunk('movies/new-version', async ({ movieId, body }, { rejectWithValue }) => {
    try {
        const response = await api.post(`/movies/${movieId}/versions`, body);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to create version');
    }
});

export const updateVersion = createAsyncThunk('movies/update-version', async ({ movieId, versionId, body }, { rejectWithValue }) => {
    try {
        const response = await api.put(`/movies/${movieId}/versions/${versionId}`, body);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to update version');
    }
});

export const deleteVersion = createAsyncThunk('movies/delete-version', async ({ movieId, versionId }) => {
    const response = await api.delete(`/movies/${movieId}/versions/${versionId}`);
    return response.data;
});

export const fetchHomefeed = createAsyncThunk('movies/homefeed', async ({ type, silent = false, organizationId }) => {
    const response = await api.get(`/homefeed/${type}`, organizationId ? { params: { organizationId } } : undefined);
    return response.data;
});

const moviesSlice = createSlice({
    name: 'movies',
    initialState: {
        list: [],
        status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        error: null,
        pagination: {
            currentPage: 1,
            totalPages: 0,
            totalCount: 0,
            limit: 50,
            hasMore: false
        },
        movie: null,
        movieStatus: 'idle', // 'idle' | 'loading' | 'success' | 'failed',
        movieError: null,
        newVersionStatus: 'idle',
        newVersionError: null,
        isDeleting: false,
        homefeeds: {
            'highlight': { data: null, status: 'idle', error: null },
            'coming-soon': { data: null, status: 'idle', error: null },
            'now-showing': { data: null, status: 'idle', error: null },
            'purchases': { data: null, status: 'idle', error: null },
            'trailers': { data: null, status: 'idle', error: null },
        },
        homepageScrollY: 0,
        homepageSearchQuery: '',
    },
    reducers: {
        setHomepageScrollY: (state, action) => {
            state.homepageScrollY = action.payload;
        },
        setHomepageSearchQuery: (state, action) => {
            state.homepageSearchQuery = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMovies.pending, (state) => {
                state.status = 'loading';
                state.list = [];
            })
            .addCase(fetchMovies.fulfilled, (state, action) => {
                state.status = 'success';
                state.list = sortByNewest(action.payload.data || action.payload);
                state.pagination = action.payload.pagination || state.pagination;
                state.error = null;
            })
            .addCase(fetchMovies.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
                state.list = [];
            })

            .addCase(fetchMovie.pending, (state) => {
                state.movieStatus = 'loading';
            })
            .addCase(fetchMovie.fulfilled, (state, action) => {
                state.movieStatus = 'success';
                state.movie = action.payload;
                state.movieError = null;
            })
            .addCase(fetchMovie.rejected, (state, action) => {
                state.movieStatus = 'failed';
                state.movieError = action.error.message || 'An error occurred';
            })

            .addCase(updateMovie.fulfilled, (state, action) => {
                state.movieStatus = 'success';
                // The update endpoint doesn't return versions (they're separate
                // documents), so keep the ones already loaded in state.
                state.movie = { ...action.payload, versions: state.movie?.versions };
                state.movieError = null;
            })
            .addCase(updateMovie.rejected, (state, action) => {
                state.movieStatus = 'failed';
                state.movieError = action.error.message || 'An error occurred';
            })

            .addCase(postNewVersion.pending, (state) => {
                state.newVersionStatus = 'loading';
            })
            .addCase(postNewVersion.fulfilled, (state, action) => {
                state.newVersionStatus = 'success';
                if (state.movie?.versions && Array.isArray(state.movie.versions)) {
                    state.movie.versions.push(action.payload);
                }
            })
            .addCase(postNewVersion.rejected, (state, action) => {
                state.newVersionStatus = 'failed';
                state.newVersionError = action.payload || 'An error occurred';
            })

            .addCase(deleteVersion.pending, (state) => {
                state.isDeleting = true;
            })
            .addCase(deleteVersion.fulfilled, (state, action) => {
                const { versionId } = action.meta.arg;
                state.movie.versions = state.movie.versions.filter(version => version._id !== versionId);
                state.isDeleting = false;
            })
            .addCase(deleteVersion.rejected, (state, action) => {
                state.isDeleting = false;
                state.newVersionError = action.error.message || 'An error occurred';
            })

            .addCase(updateVersion.pending, (state) => {
                state.newVersionStatus = 'loading';
            })
            .addCase(updateVersion.fulfilled, (state, action) => {
                const { versionId } = action.meta.arg;
                state.movie.versions = state.movie.versions.map(version =>
                    version._id === versionId ? action.payload : version
                );
                state.newVersionStatus = 'success';
            })
            .addCase(updateVersion.rejected, (state, action) => {
                state.newVersionStatus = 'failed';
                state.newVersionError = action.payload || 'An error occurred';
            })

            .addCase(fetchHomefeed.pending, (state, action) => {
                const { type, silent } = action.meta.arg;
                if (!silent) {
                    state.homefeeds[type].status = 'loading';
                }
            })
            .addCase(fetchHomefeed.fulfilled, (state, action) => {
                const { type } = action.meta.arg;
                state.homefeeds[type].data = action.payload;
                state.homefeeds[type].status = 'success';
            })
            .addCase(fetchHomefeed.rejected, (state, action) => {
                const { type, silent } = action.meta.arg;
                if (!silent) {
                    state.homefeeds[type].status = 'failed';
                    state.homefeeds[type].error = action.error.message || 'An error occurred';
                }
            })
        ;
    },
});

export const { setHomepageScrollY, setHomepageSearchQuery } = moviesSlice.actions;

export default moviesSlice.reducer;
